import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { useModalControl } from '@frontend/design-system';
import { useCheckPaymentsEnabled } from '../../../../../../hooks/use-check-payments-enabled';
import { ScheduleRequestModalContextType } from '../Context/ScheduleRequestModalContext';

export const useScheduleRequestModalContextValue = <T extends ScheduleRequest = ScheduleRequest>({
  refetchScheduleRequestList,
  selectedRequest: selectedRequestProp,
  setSelectedRequest: setSelectedRequestProp,
}: {
  refetchScheduleRequestList: () => void;
  selectedRequest?: T;
  setSelectedRequest?: Dispatch<SetStateAction<T | null>>;
}) => {
  const [selectedScheduleRequest, setSelectedScheduleRequest] = useState<T | null>(selectedRequestProp ?? null);
  const selectedRequest = setSelectedRequestProp ? selectedRequestProp : selectedScheduleRequest;
  const setSelectedRequest = setSelectedRequestProp || setSelectedScheduleRequest;

  const scheduleRequestModalProps = useModalControl({
    onClose: () => {
      setTimeout(() => {
        setSelectedRequest(null);
      }, 200); // Delay to allow modal's exit animation to finish
    },
  });
  const { isPaymentsEnabled } = useCheckPaymentsEnabled(selectedRequest?.locationId);

  const handleCloseScheduleRequestModal = useCallback(() => {
    scheduleRequestModalProps.closeModal();
  }, [scheduleRequestModalProps]);

  const scheduleRequestModalContextValue = useMemo<ScheduleRequestModalContextType>(
    () => ({
      scheduleRequestModalProps,
      selectedScheduleRequest: selectedRequest as T,
      handleCloseScheduleRequestModal,
      refetchScheduleRequestList,
      isPaymentsEnabled,
    }),
    [
      selectedRequest,
      refetchScheduleRequestList,
      isPaymentsEnabled,
      handleCloseScheduleRequestModal,
      scheduleRequestModalProps,
    ]
  );

  return {
    selectedScheduleRequest: selectedRequest as T,
    setSelectedScheduleRequest: setSelectedRequest,
    scheduleRequestModalContextValue,
    isPaymentsEnabled,
  };
};
